import { Component, EventEmitter, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { SegmentService } from 'src/app/services/segment/segment.service';

@Component({
  selector: 'app-billing-calculator',
  templateUrl: './billing-calculator.component.html',
  styleUrls: ['./billing-calculator.component.scss'],
})
export class BillingCalculatorComponent {
  @Output() usersChange = new EventEmitter<number | null>();

  // Limit to 6 digits as requested
  users: number | null = 1;
  minUsers = 1;
  maxUsersDigits = 6;
  maxUsers = 10 ** this.maxUsersDigits - 1; // 999999
  basePlaceholder = '1';
  showPh = true;

  // Debounced manual input tracking trigger
  private manualInput$ = new Subject<number>();

  constructor(private readonly segment: SegmentService) {
    // Fire tracking 1s after the user stops typing
    this.manualInput$.pipe(
      debounceTime(1000),
      filter(v => typeof v === 'number' && !isNaN(v) && v >= this.minUsers),
    ).subscribe((count) => {
      this.segment.trackInAppEvents('Changed Users Selection On Plan Page', {
        usercount: count,
      });
    });
  }

  private emitUsers() {
    this.usersChange.emit(this.users);
  }

  step(delta: number) {
    const next = (Number(this.users) || 0) + delta;
    const clamped = Math.max(this.minUsers, Math.min(this.maxUsers, next));
    this.users = clamped;
    this.emitUsers();
  }

  // Prevent typing of anything except digits and control/navigation keys
  preventInvalidKeys(event: KeyboardEvent): void {
    const allowedControlKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End', 'Enter'];
    if (allowedControlKeys.includes(event.key)) return;

    // Allow common shortcuts (copy/paste/select all/cut)
    if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())) return;

    // Block minus, plus, exponent, decimal separators, and any non-digit
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    // Enforce max 6 digits while typing (respect selected range replacement)
    const input = event.target as HTMLInputElement;
    const value = input.value ?? '';
    const selStart = input.selectionStart ?? value.length;
    const selEnd = input.selectionEnd ?? value.length;
    const replacing = Math.max(0, selEnd - selStart);
    const prospectiveLength = value.replace(/\D/g, '').length - replacing + 1; // adding one digit
    if (prospectiveLength > this.maxUsersDigits) {
      event.preventDefault();
      return;
    }
  }

  shouldShowPlaceholder(): boolean {
    return this.users == null || isNaN(this.users as any);
  }

  // Clamp value in real time to avoid negatives and enforce 6 digits
  onUsersInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value;

    // Allow the user to clear the field completely. Keep the model as null and don't force a value.
    if (raw.trim() === '') {
      this.users = null;
      this.emitUsers();
      return;
    }

    // Keep only digits and limit to maxUsersDigits
    const digitsOnly = raw.replace(/\D/g, '');
    const limited = digitsOnly.slice(0, this.maxUsersDigits);

    if (limited.length === 0) {
      this.users = null;
      input.value = '';
      this.emitUsers();
      return;
    }

    const intVal = Math.floor(Number(limited));
    const clamped = Math.max(this.minUsers, Math.min(this.maxUsers, intVal));

    const shouldNormalize = limited !== raw || String(intVal) !== limited || clamped !== intVal;

    if (shouldNormalize) {
      // Update both the model and the input to keep them in sync and ensure integer-only display
      this.users = clamped;
      input.value = String(clamped);
    } else {
      this.users = intVal;
    }

    this.emitUsers();

    // Debounced manual input tracking (only for typing, not for clicks)
    if (this.users != null) {
      this.manualInput$.next(this.users);
    }
  }

  // Block pasting of negative or non-numeric values (length trimming handled in input)
  onUsersPaste(event: ClipboardEvent): void {
    const text = event.clipboardData?.getData('text') ?? '';
    if (!/^\d+$/.test(text)) {
      event.preventDefault();
      return;
    }
  }
}
